export type StarterCategory =
  'Campaigns' | 'Planning' | 'Flows' | 'Segments' | 'Quality' | 'Insights';

export const STARTER_CATEGORIES: readonly StarterCategory[] = [
  'Campaigns',
  'Planning',
  'Flows',
  'Segments',
  'Quality',
  'Insights',
];

export interface StarterPrompt {
  id: string;
  category: StarterCategory;
  title: string;
  description: string;
  /** The first [bracket] is selected when the prompt lands in the chat box. */
  body: string;
}

/**
 * Ready-made prompts for every company. The assistant only writes text and
 * cannot read the account, so each prompt asks for the facts to be pasted in.
 * The brand profile is already part of the assistant's instructions, so these
 * never repeat it. At most 2,000 characters each, the chat box limit.
 */
export const STARTER_PROMPTS: readonly StarterPrompt[] = [
  {
    id: 'subject-lines',
    category: 'Campaigns',
    title: 'Subject lines to A/B test',
    description: 'Eight subject lines from four angles, and which two to test first.',
    body: `Write 8 subject lines with preview text for a mail to [who receives it] about [topic].
Use four angles, two lines each: curiosity, exclusivity, plain value and story. Keep subject lines under 60 characters.
Name the angle for each line. Then say which two you would A/B test first and why.`,
  },
  {
    id: 'campaign-from-brief',
    category: 'Campaigns',
    title: 'Full campaign from a brief',
    description: 'Audience, subject lines and the complete mail from a short brief.',
    body: `Build a campaign from this brief: [goal, product and offer].
Give me:
1. Who should receive it and who to leave out, to protect the list.
2. Three subject lines and three preview texts, each under 60 characters.
3. The full mail: a top section, supporting sections and one main button.
4. The best day and time to send, with your reason.
Only use a discount if the brief asks for one. If something you need is missing, ask before you write.`,
  },
  {
    id: 'product-launch',
    category: 'Campaigns',
    title: 'Product launch mail',
    description: 'A launch mail that opens with the change the product makes.',
    body: `Write a launch mail for [product].
Who receives it: [audience]. Launch date: [date]. What it solves: [problem]. Main benefits: [3 to 5 points]. Offer: [price, bonus or guarantee]. Main button: [action].
Order: open with the change the customer gets, not the product name. Show what they miss today. Present the product as the answer. Add proof, using only facts I gave you. Give the value before the price. End with one button.
Write 3 subject lines as well, and do not invent numbers, reviews or claims.`,
  },
  {
    id: 'no-discount',
    category: 'Campaigns',
    title: 'Mail that sells without a discount',
    description: 'A sales mail built on use, proof and timing rather than a code.',
    body: `Write a mail that sells [product] without any discount, to [audience].
Reason to buy now: [occasion or reason]. Build it on how the product is used, what customers worry about and the facts in the brand profile. Keep it under 150 words, with one button. Add 3 subject lines.`,
  },
  {
    id: 'shorten',
    category: 'Campaigns',
    title: 'Make a mail shorter and clearer',
    description: 'Cuts a draft down and tells you what it removed.',
    body: `Make this mail shorter and clearer without changing facts, prices or the offer. Keep one main action. Then list what you removed and why.

[paste the mail text]`,
  },
  {
    id: 'calendar',
    category: 'Planning',
    title: '30-day send plan',
    description: 'A month of sends that balances revenue and list health.',
    body: `Make a 30-day mail plan.
Products and seasons to work with: [details]. What we sent recently: [last sends]. Dates that matter: [dates].
Give me a table with at most 12 sends: date, goal (revenue, education, retention or reviews), audience, offer if any, and subject line angle.
Do not lean on discounts. Mix sales mails with useful ones. Point out any send that should be an automated flow instead.`,
  },
  {
    id: 'black-friday',
    category: 'Planning',
    title: 'Black Friday offer plan',
    description: 'The offer that earns the most, not simply the biggest discount.',
    body: `Plan our Black Friday offer. The goal is the best result over time, not the biggest discount.
Best sellers: [products]. Average order: [amount]. Margin limits: [limits]. What we did last year and how it went: [details]. Main goal: [for example larger orders or new customers].
Compare three offer types, with the risk of each. Recommend one. Then give a send plan from one week before until the Monday after, with an early offer for our best customers.`,
  },
  {
    id: 'poke-holes',
    category: 'Planning',
    title: 'Poke holes in a plan',
    description: 'Finds the weak spots in a plan before you commit to it.',
    body: `Find the weak spots in this mail plan. Be direct. List the three biggest risks first, then what you would change, and say what you would need to know to be sure.

[paste the plan]`,
  },
  {
    id: 'new-flow',
    category: 'Flows',
    title: 'Design a flow from a goal',
    description: 'A numbered build plan with trigger, timing and subject lines.',
    body: `Design an automated flow for this goal: [for example, win back customers who have not bought for 90 days].
Give a numbered plan: what starts the flow and who is left out, how many mails and the wait between them, the job of each mail, when someone leaves the flow so it does not overlap other flows, and a subject line for each mail.
Say what you are assuming about our other flows. Do not assume this replaces one of them.`,
  },
  {
    id: 'second-purchase',
    category: 'Flows',
    title: 'Turn deal buyers into repeat customers',
    description: 'A follow-up for first-time buyers that came in on a discount.',
    body: `Many first-time buyers came in on a discount during [campaign]. Plan a flow that gets a second order at full price.
Our products: [products]. Usual time between orders: [time].
Say who enters it, when each mail goes out and what it says. Lead with product advice and value, and only offer a discount as the last step. Say what to cross-sell after each product, and which number will show that it works.`,
  },
  {
    id: 'clickers',
    category: 'Flows',
    title: 'Convert people who click but never buy',
    description: 'Three mails built around why these people hesitate.',
    body: `Some contacts click our mails often but rarely buy. They mostly click on: [products or topics].
Give the likely reasons they hesitate. Then write a plan for 3 mails, each with an angle, an offer if it is needed, and a subject line, built around those reasons. Also give the segment rules to find them.`,
  },
  {
    id: 'segment-ideas',
    category: 'Segments',
    title: 'Segment ideas',
    description: 'Prioritised segments with the rules to build them.',
    body: `Suggest the 5 segments that would earn us the most. Today we mostly send to: [current audiences]. What we know about contacts: language, country, orders, last order, order value and mail engagement.
For each one give the rules, why it is worth a separate send, and the first mail to send it. Put the most valuable one first.`,
  },
  {
    id: 'explain-segment',
    category: 'Segments',
    title: 'Explain a segment in plain words',
    description: 'Says who is in a segment and spots conflicting rules.',
    body: `Explain in plain words who is in this segment and who is left out. Point out rules that conflict or do nothing.

[paste the segment rules]`,
  },
  {
    id: 'pre-send',
    category: 'Quality',
    title: 'Pre-send check',
    description: 'A checklist with a clear go or no-go before you schedule.',
    body: `Check this mail before it goes out. Audience: [who]. Offer: [what, including dates and code].
Check that: subject line and preview text are under 60 characters; links do not look like placeholders or test addresses; merge tags have a fallback; prices, codes and dates match the offer; the unsubscribe link and company address are there.
You cannot open links, so say what I must click myself. Start with go or no-go. Separate what blocks the send from small fixes.

[paste the subject line and mail]`,
  },
  {
    id: 'brand-check',
    category: 'Quality',
    title: 'Does this sound like us?',
    description: 'Checks a text against the brand profile and rewrites what is off.',
    body: `Check this text against our brand profile: tone, words we avoid, claims we never make and facts that must be exact. Quote each line that breaks a rule and give a corrected version. If nothing is wrong, say so.

[paste the text]`,
  },
  {
    id: 'reviews',
    category: 'Insights',
    title: 'What our reviews tell us',
    description: 'Turns customer reviews into worries, wishes and mail angles.',
    body: `Here are customer reviews with names removed. Find what customers worry about, what they want, why they buy, why they hesitate and the words they use themselves. Quote short examples. End with 5 mail angles and 5 subject lines in their own words.

[paste the reviews, without names]`,
  },
  {
    id: 'top-subjects',
    category: 'Insights',
    title: 'Learn from our best subject lines',
    description: 'Finds the pattern in your top subject lines and writes eight more.',
    body: `These are our subject lines with the best and worst results. What do the best have in common, and what do the worst have in common? Say how sure you are, given how few there are. Then write 8 new ones about [topic].

[paste subject lines with their open or click rates]`,
  },
];
